import Spinner from 'react-bootstrap/Spinner';
import CmsPageSkeleton from '../components/CmsPageSkeleton';

import {
    FiZap, FiSave, FiTag, FiImage, FiInfo,
} from 'react-icons/fi';

import toast from 'react-hot-toast';
import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../lib/StAuthGiver';
import { callApi, getCurSiteId } from '../lib/Common_funcs.js';

const WhichEnv = (process.env.REACT_APP_TURBO_ENV === undefined ? 'aws' : 'staging');
const Tdebug = (WhichEnv !== 'aws');

export const StCmsBrand = () => {
    const { loginAction, logOut } = useAuth();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [siteId] = useState(getCurSiteId() || '');

    // Brand profile fields
    const [tone, setTone] = useState('');
    const [industry, setIndustry] = useState('');
    const [keywords, setKeywords] = useState([]);
    const [keywordInput, setKeywordInput] = useState('');
    const [logoUrl, setLogoUrl] = useState('');

    // Metadata
    const [createdAt, setCreatedAt] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [isDefault, setIsDefault] = useState(true);

    const isMounted = useRef(false);

    const industries = [
        'Technology / SaaS',
        'E-commerce / Retail',
        'Healthcare',
        'Finance / Banking',
        'Education',
        'Real Estate',
        'Travel / Hospitality',
        'Food & Beverage',
        'Fashion / Apparel',
        'Media / Entertainment',
        'Manufacturing',
        'Professional Services',
        'Non-profit',
        'Other'
    ];

    useEffect(() => {
        if (isMounted.current) return;
        isMounted.current = true;

        fetchBrandProfile();
    }, []);

    const fetchBrandProfile = async () => {
        try {
            setLoading(true);
            const url = `/sites/${siteId}/cms/brand`;
            const rcJson = await callApi(url, 'GET', {}, {});

            if (rcJson.disposition === 'logout') {
                logOut();
                return;
            }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200) {
                // API may return { success, data: {...} } or direct data
                const data = rcJson.respBody.data || rcJson.respBody;

                // Handle tone - can be array or string
                if (Array.isArray(data.tone)) {
                    setTone(data.tone.join(', '));
                } else {
                    setTone(data.tone || '');
                }

                setIndustry(data.industry || '');
                setKeywords(data.keywords || []);
                setLogoUrl(data.logo_url || '');
                setCreatedAt(data.created_at);
                setUpdatedAt(data.updated_at);
                setIsDefault(data.is_default !== false);

                if (rcJson.respBody?.etac) {
                    loginAction(null, rcJson.respBody.etac);
                }
            }
        } catch (err) {
            Tdebug && console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const saveBrandProfile = async () => {
        try {
            setSaving(true);

            // Parse tone into array
            const toneArray = tone
                .split(',')
                .map(t => t.trim())
                .filter(t => t.length > 0)
                .slice(0, 5); // Max 5 tone descriptors

            const requestBody = {
                tone: toneArray,
                industry: industry,
                keywords: keywords.slice(0, 20), // Max 20 keywords
                logo_url: logoUrl
            };

            const url = `/sites/${siteId}/cms/brand`;
            const rcJson = await callApi(url, 'PUT', {}, JSON.stringify(requestBody));

            if (rcJson.disposition === 'logout') {
                logOut();
                return;
            }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200) {
                toast.success('Brand profile saved!');
                setIsDefault(false);
                setUpdatedAt(new Date().toISOString());

                if (rcJson.respBody?.etac) {
                    loginAction(null, rcJson.respBody.etac);
                }
            } else {
                toast.error(rcJson.message || 'Failed to save brand profile');
            }
        } catch (err) {
            Tdebug && console.log(err);
            toast.error('Failed to save brand profile');
        } finally {
            setSaving(false);
        }
    };

    // Keyword handling
    const addKeyword = () => {
        const newKeyword = keywordInput.trim().toLowerCase();
        if (newKeyword && !keywords.includes(newKeyword) && keywords.length < 20) {
            setKeywords([...keywords, newKeyword]);
        }
        setKeywordInput('');
    };

    const removeKeyword = (keywordToRemove) => {
        setKeywords(keywords.filter(k => k !== keywordToRemove));
    };

    const handleKeywordKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addKeyword();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const inputStyle = {
        width: '100%', padding: '9px 12px', border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-md)', fontSize: '13px', background: 'var(--bg-primary)',
        color: 'var(--text-primary)', fontFamily: 'inherit', outline: 'none',
    };
    const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' };
    const helpStyle = { display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '6px' };

    if (loading) {
        return <CmsPageSkeleton />;
    }

    return (
        <div className="app-content animate-in">
            {/* Header */}
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h1 className="dashboard-title"><FiZap size={20} /> AI Settings</h1>
                    <p className="dashboard-subtitle">Configure your brand voice for AI-generated content</p>
                </div>
                <button
                    onClick={saveBrandProfile}
                    disabled={saving}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', background: 'var(--accent-gradient)', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? 0.7 : 1 }}
                >
                    {saving ? <><Spinner size="sm" />Saving...</> : <><FiSave size={14} /> Save Profile</>}
                </button>
            </div>

            {/* Info banner */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: 'var(--accent-soft)', border: '1px solid rgba(37,99,235,0.15)', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: '24px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                <FiInfo size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: '1px' }} />
                <span>Your brand profile helps AI generate content that matches your brand's voice and style.</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: '16px' }}>
                {/* Left column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Brand Voice */}
                    <div className="bento-card">
                        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 16px' }}>Brand Voice & Tone</h3>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>Tone Descriptors</label>
                            <textarea
                                rows={3}
                                placeholder="Professional, friendly, innovative, approachable, confident"
                                value={tone}
                                onChange={(e) => setTone(e.target.value)}
                                style={{ ...inputStyle, resize: 'vertical' }}
                            />
                            <span style={helpStyle}>Enter up to 5 words or phrases that describe your brand's voice (comma-separated).</span>
                        </div>
                        <div>
                            <label style={labelStyle}>Industry</label>
                            <select value={industry} onChange={(e) => setIndustry(e.target.value)} style={inputStyle}>
                                <option value="">Select your industry</option>
                                {industries.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                            </select>
                            <span style={helpStyle}>Helps AI understand context and use appropriate terminology.</span>
                        </div>
                    </div>

                    {/* Keywords */}
                    <div className="bento-card">
                        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiTag size={16} /> Target Keywords</h3>
                        <label style={labelStyle}>SEO Keywords</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input
                                type="text"
                                placeholder="Add a keyword"
                                value={keywordInput}
                                onChange={(e) => setKeywordInput(e.target.value)}
                                onKeyDown={handleKeywordKeyDown}
                                style={{ ...inputStyle, flex: 1 }}
                            />
                            <button
                                onClick={addKeyword}
                                disabled={keywords.length >= 20}
                                style={{ padding: '9px 16px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text-primary)', flexShrink: 0 }}
                            >
                                Add
                            </button>
                        </div>
                        <span style={helpStyle}>Keywords AI should incorporate into generated content ({keywords.length}/20).</span>
                        {keywords.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                                {keywords.map((keyword, index) => (
                                    <span
                                        key={index}
                                        onClick={() => removeKeyword(keyword)}
                                        className="badge-modern"
                                        style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                    >
                                        {keyword} &times;
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Logo */}
                    <div className="bento-card">
                        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiImage size={16} /> Logo</h3>
                        {logoUrl ? (
                            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                                <img
                                    src={logoUrl}
                                    alt="Brand Logo"
                                    style={{ maxHeight: '100px', maxWidth: '100%', borderRadius: 'var(--radius-md)' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', marginBottom: '12px', padding: '28px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', border: '2px dashed var(--border-color)' }}>
                                <FiImage size={28} style={{ color: 'var(--text-tertiary)', marginBottom: '8px' }} />
                                <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', margin: 0 }}>No logo set</p>
                            </div>
                        )}
                        <label style={labelStyle}>Logo URL</label>
                        <input
                            type="url"
                            placeholder="https://example.com/logo.png"
                            value={logoUrl}
                            onChange={(e) => setLogoUrl(e.target.value)}
                            style={inputStyle}
                        />
                        <span style={helpStyle}>Enter the URL of your logo image.</span>
                        {logoUrl && (
                            <button
                                onClick={() => setLogoUrl('')}
                                style={{ width: '100%', marginTop: '10px', padding: '8px', background: 'var(--error-soft)', border: '1px solid var(--error-soft)', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--error)' }}
                            >
                                Remove Logo
                            </button>
                        )}
                    </div>

                    {/* Status */}
                    <div className="bento-card">
                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: updatedAt ? '6px' : 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            Status:
                            {isDefault
                                ? <span className="badge-modern" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>Default</span>
                                : <span className="badge-modern badge-modern-success">Customized</span>}
                        </div>
                        {updatedAt && (
                            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Last updated: {formatDate(updatedAt)}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StCmsBrand;
